const RAW_URL_REGEX = /(https?:\/\/[^\s]+)/g;
const TRAILING_PUNCTUATION_REGEX = /[),.!?;:]+$/;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const getAdminLessonHref = (href) => {
    if (!isBrowser() || !href) return href;

    try {
        const url = new URL(href, window.location.origin);
        const match = url.pathname.match(/^\/student\/lessons\/([^/]+)$/);

        if (!match || url.origin !== window.location.origin) {
            return href;
        }

        return `/admin/lessons/${match[1]}/view${url.search}${url.hash}`;
    } catch {
        return href;
    }
};

const prepareAnchor = (anchor, context) => {
    const href = anchor.getAttribute('href');
    const nextHref = context === 'admin' ? getAdminLessonHref(href) : href;

    if (nextHref) {
        anchor.setAttribute('href', nextHref);
    }

    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
};

const linkifyTextNode = (textNode, context) => {
    const text = textNode.nodeValue;
    RAW_URL_REGEX.lastIndex = 0;
    const matches = [...text.matchAll(RAW_URL_REGEX)];

    if (matches.length === 0) return;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    matches.forEach((match) => {
        const rawUrl = match[0];
        const startIndex = match.index;
        const trailing = rawUrl.match(TRAILING_PUNCTUATION_REGEX)?.[0] || '';
        const cleanUrl = trailing ? rawUrl.slice(0, -trailing.length) : rawUrl;

        if (startIndex > lastIndex) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex, startIndex)));
        }

        const anchor = document.createElement('a');
        anchor.href = cleanUrl;
        anchor.textContent = cleanUrl;
        prepareAnchor(anchor, context);
        fragment.appendChild(anchor);

        if (trailing) {
            fragment.appendChild(document.createTextNode(trailing));
        }

        lastIndex = startIndex + rawUrl.length;
    });

    if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode.replaceChild(fragment, textNode);
};

export const prepareHomeworkHtml = (html, context = 'student') => {
    if (!html) return '';

    if (!isBrowser() || typeof DOMParser === 'undefined') {
        return html;
    }

    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;

    if (!root) return html;

    root.querySelectorAll('a').forEach((anchor) => prepareAnchor(anchor, context));

    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
            if (!node.nodeValue || !RAW_URL_REGEX.test(node.nodeValue)) {
                RAW_URL_REGEX.lastIndex = 0;
                return NodeFilter.FILTER_REJECT;
            }

            RAW_URL_REGEX.lastIndex = 0;
            return node.parentElement?.closest('a')
                ? NodeFilter.FILTER_REJECT
                : NodeFilter.FILTER_ACCEPT;
        },
    });

    const textNodes = [];
    while (walker.nextNode()) {
        textNodes.push(walker.currentNode);
    }

    textNodes.forEach((node) => linkifyTextNode(node, context));

    return root.innerHTML;
};
